/**
 * Распознавание структуры: титульная часть, содержание, заголовки, подписи,
 * рисунки, таблицы, формулы, фрагменты кода, список источников и ссылки на него.
 *
 * Работает и по стилям Word, и эвристически — для документов, где
 * «заголовок» — это просто жирная строка с номером или без него.
 */

import type { DocModel, Paragraph, Table } from '../model/document';
import { collapseSpaces, OBJ } from '../util/text';
import { letterCount, paraFormat } from './format';

export type Role =
  | 'front'
  | 'toc'
  | 'heading'
  | 'appendix-title'
  | 'body'
  | 'list'
  | 'caption'
  | 'figure'
  | 'formula'
  | 'formula-where'
  | 'bib'
  | 'note'
  | 'table-cell'
  | 'code'
  | 'empty';

export type Region = 'front' | 'toc' | 'main' | 'bibliography' | 'appendix';

export type StructuralKind =
  | 'executors'
  | 'abstract'
  | 'annotation'
  | 'preface'
  | 'contents'
  | 'terms'
  | 'abbreviations'
  | 'intro'
  | 'conclusion'
  | 'references'
  | 'appendix';

/** Тип работы: от него зависит, какие структурные элементы обязательны. */
export type DocKind = 'thesis' | 'coursework' | 'report' | 'essay' | 'lab' | 'practical' | 'control' | 'unknown';

export const DOC_KIND_TITLES: Record<DocKind, string> = {
  thesis: 'Выпускная квалификационная работа',
  coursework: 'Курсовая работа',
  report: 'Отчёт',
  essay: 'Реферат',
  lab: 'Лабораторная работа',
  practical: 'Практическая работа',
  control: 'Контрольная работа',
  unknown: 'Документ',
};

/** Для этих типов работ структурные требования к курсовым и ВКР не применяются. */
export const SHORT_WORK_KINDS: ReadonlySet<DocKind> = new Set(['lab', 'practical', 'control']);

export const STRUCTURAL_TITLES: Record<StructuralKind, string> = {
  executors: 'СПИСОК ИСПОЛНИТЕЛЕЙ',
  abstract: 'РЕФЕРАТ',
  annotation: 'АННОТАЦИЯ',
  preface: 'ПРЕДИСЛОВИЕ',
  contents: 'СОДЕРЖАНИЕ',
  terms: 'ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ',
  abbreviations: 'ПЕРЕЧЕНЬ СОКРАЩЕНИЙ И ОБОЗНАЧЕНИЙ',
  intro: 'ВВЕДЕНИЕ',
  conclusion: 'ЗАКЛЮЧЕНИЕ',
  references: 'СПИСОК ИСПОЛЬЗОВАННЫХ ИСТОЧНИКОВ',
  appendix: 'ПРИЛОЖЕНИЕ',
};

const STRUCTURAL_PATTERNS: Array<[StructuralKind, RegExp]> = [
  ['contents', /^(СОДЕРЖАНИЕ|ОГЛАВЛЕНИЕ)$/],
  ['intro', /^ВВЕДЕНИЕ$/],
  ['conclusion', /^(ЗАКЛЮЧЕНИЕ|ВЫВОДЫ)$/],
  [
    'references',
    /^(СПИСОК (ИСПОЛЬЗОВАННЫХ |ИСПОЛЬЗУЕМЫХ |ЦИТИРУЕМЫХ )?(ИСТОЧНИКОВ|ЛИТЕРАТУРЫ)( И ЛИТЕРАТУРЫ| И ИСТОЧНИКОВ)?|СПИСОК ИСПОЛЬЗОВАННОЙ ЛИТЕРАТУРЫ( И ИСТОЧНИКОВ)?|БИБЛИОГРАФИЧЕСКИЙ СПИСОК|БИБЛИОГРАФИЯ|ЛИТЕРАТУРА|ИСПОЛЬЗОВАННАЯ ЛИТЕРАТУРА)$/,
  ],
  ['abstract', /^РЕФЕРАТ$/],
  ['annotation', /^АННОТАЦИЯ$/],
  ['preface', /^ПРЕДИСЛОВИЕ$/],
  ['terms', /^ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ$/],
  [
    'abbreviations',
    /^((ПЕРЕЧЕНЬ|СПИСОК) (СОКРАЩЕНИЙ|УСЛОВНЫХ ОБОЗНАЧЕНИЙ|ОБОЗНАЧЕНИЙ)( И (ОБОЗНАЧЕНИЙ|СОКРАЩЕНИЙ|УСЛОВНЫХ ОБОЗНАЧЕНИЙ))?|ОПРЕДЕЛЕНИЯ, ОБОЗНАЧЕНИЯ И СОКРАЩЕНИЯ|ОБОЗНАЧЕНИЯ И СОКРАЩЕНИЯ|СОКРАЩЕНИЯ И ОБОЗНАЧЕНИЯ)$/,
  ],
  ['executors', /^СПИСОК ИСПОЛНИТЕЛЕЙ$/],
];

const APPENDIX_RE = /^ПРИЛОЖЕНИЕ(?:\s+([А-ЯЁA-Z]|\d{1,2}))?(?=$|[\s.(])/i;

export interface Heading {
  paragraph: number;
  /** 1 — структурный элемент или раздел, 2 — подраздел и т. д. */
  level: number;
  kind: 'structural' | 'section' | 'appendix';
  structural?: StructuralKind;
  /** Номер раздела: «1», «1.2». */
  number?: string;
  numberSource?: 'auto' | 'text';
  /** Точка после номера в тексте или в шаблоне автонумерации. */
  numberDot: boolean;
  title: string;
  byStyle: boolean;
  appendixLetter?: string;
  /** Заголовок угадан по оформлению (короткая полужирная строка без номера и стиля). */
  inferred?: boolean;
}

export interface Caption {
  paragraph: number;
  kind: 'figure' | 'table';
  continuation: boolean;
  /** Слово как написано: «Рисунок», «Рис.», «Таблица», «Табл.». */
  word: string;
  number: string;
  /** Разделитель между номером и наименованием: «—», «-», «.», «:» или пусто. */
  sep: string;
  title: string;
  /** Индекс абзаца с рисунком или индекс таблицы. */
  target?: number;
  /** Подпись стоит не на своём месте (над рисунком или под таблицей). */
  misplaced: boolean;
}

export interface Citation {
  paragraph: number;
  start: number;
  end: number;
  numbers: number[];
  raw: string;
}

export interface BibEntry {
  paragraph: number;
  number?: number;
  label?: string;
  auto: boolean;
  text: string;
}

export interface Formula {
  paragraph: number;
  number?: string;
}

export interface Structure {
  kind: DocKind;
  roles: Role[];
  regions: Region[];
  headings: Heading[];
  headingByPara: Map<number, Heading>;
  captions: Caption[];
  captionByPara: Map<number, Caption>;
  /** Абзацы с рисунками в основной части и приложениях. */
  figures: number[];
  /** Таблицы с данными (без служебных таблиц разметки и блоков кода). */
  dataTables: number[];
  formulas: Formula[];
  bibliography?: { heading: number; entries: BibEntry[] };
  citations: Citation[];
  tocEntries: number[];
  tocAuto: boolean;
  /** Индекс первого абзаца после титульной части. */
  frontEnd: number;
  hasTitlePage: boolean;
}

const FIGURE_KINDS = new Set(['image', 'chart', 'diagram', 'group', 'shape', 'ole']);

export function isFigureParagraph(p: Paragraph): boolean {
  return p.objects.some((o) => FIGURE_KINDS.has(o.kind) && (o.widthMm === undefined || o.widthMm > 15));
}

/** Текст абзаца со схлопнутыми пробелами, без мягких переносов. */
export function cleanText(p: Paragraph): string {
  return collapseSpaces(p.text.replace(/\u00ad/g, ''));
}

function normalizeTitle(s: string): string {
  return collapseSpaces(s.replace(/\ufffc/g, ''))
    .toUpperCase()
    .replace(/Ё/g, 'Е')
    .replace(/[.:;]+$/, '')
    .trim();
}

const NUMBER_PREFIX_RE = /^(\d{1,2}(?:\.\d{1,2}){0,3})(\.?)\s+(?=\S)/;
const TOC_LINE_RE = /(?:\t|\.{2,}|…|_{2,}|\s{2,})\s*\d{1,3}\s*$/;

/** Номер из автонумерации: «1.», «2.3» → «1», «2.3». */
function autoNumber(p: Paragraph): { number: string; dot: boolean } | undefined {
  if (!p.list || p.list.bullet) return undefined;
  const m = /^((?:[А-ЯA-Z]\.)?\d{1,2}(?:\.\d{1,2}){0,3})(\.?)$/.exec(p.list.label.trim());
  return m ? { number: m[1]!, dot: m[2] === '.' } : undefined;
}

/**
 * Заголовок приложения: «ПРИЛОЖЕНИЕ А», «Приложение Б. Листинг», «Приложение 1 (обязательное)».
 * «Приложение написано на C#» — предложение о программе, а не заголовок.
 */
function appendixHeading(line: string): { letter?: string } | undefined {
  const t = line.trim();
  if (!/^(?:ПРИЛОЖЕНИЕ|Приложение)(?![\p{L}])/u.test(t) || t.length > 150 || /[;{}]$/.test(t)) return undefined;
  const m = /^приложение(?:\s+([а-яёa-z]|\d{1,2})(?![\p{L}\d]))?\s*(.*)$/iu.exec(t);
  if (!m) return undefined;
  const rest = m[2] ?? '';
  if (!m[1] && /^[\p{Ll}]/u.test(rest)) return undefined;
  if (m[1] && /^[\p{Ll}]/u.test(rest) && !/^\(/.test(rest)) return undefined;
  // «Приложение А.1 — Экран…» — пункт внутри приложения, а не новое приложение.
  if (m[1] && /^\.\d/.test(rest)) return undefined;
  return { letter: m[1]?.toUpperCase() };
}

export function matchStructural(text: string): StructuralKind | undefined {
  const t = normalizeTitle(text);
  if (APPENDIX_RE.test(t)) return 'appendix';
  for (const [kind, re] of STRUCTURAL_PATTERNS) if (re.test(t)) return kind;
  return undefined;
}

const FIG_CAPTION_RE =
  /^(Рисунок|Рис\.?|Рисунок\s*№|Figure|Fig\.)\s*№?\s*((?:[А-ЯA-Z]\.?\s?)?\d{1,3}(?:[.-]\d{1,3})*)\s*([.:)]?)\s*(?:([-‐-―−])\s*)?(.*)$/su;
const TABLE_CAPTION_RE =
  /^(Таблица|Табл\.?|Table)\s*№?\s*((?:[А-ЯA-Z]\.?\s?)?\d{1,3}(?:[.-]\d{1,3})*)\s*([.:)]?)\s*(?:([-‐-―−])\s*)?(.*)$/su;
const TABLE_CONT_RE = /^(Продолжение|Окончание)\s+(таблицы|табл\.)\s*№?\s*((?:[А-ЯA-Z]\.?\s?)?\d{1,3}(?:[.-]\d{1,3})*)/iu;

const CITATION_RE = /\[([^[\]\n]{1,120})\]/g;
const PAGE_MARK = '(?:с|c|С|C|p|pp|P|S|s|т|Т|л|Л|стр|ст|гл|разд|табл|рис|кн|ч|vol|Vol)';
const SEG_PAGE_RE = new RegExp(`^(\\d{1,3})\\s*,?\\s*${PAGE_MARK}\\.`);
const SEG_LIST_RE = /^\d{1,3}(?:\s*[-–—]\s*\d{1,3})?(?:\s*,\s*\d{1,3}(?:\s*[-–—]\s*\d{1,3})?)*$/;

/** Номера источников в ссылке: «1, с. 25» → [1]; «1–3; 7» → [1, 2, 3, 7]. [0] ссылкой не бывает. */
export function parseCitation(inner: string): number[] | undefined {
  const nums: number[] = [];
  for (const raw of inner.split(';')) {
    const seg = raw.trim();
    if (!seg) continue;
    const page = SEG_PAGE_RE.exec(seg);
    if (page) {
      nums.push(Number(page[1]));
      continue;
    }
    if (SEG_LIST_RE.test(seg)) {
      for (const part of seg.split(',')) {
        const range = /^(\d+)\s*[-–—]\s*(\d+)$/.exec(part.trim());
        if (range) {
          const a = Number(range[1]);
          const b = Number(range[2]);
          if (b >= a && b - a <= 50) for (let k = a; k <= b; k++) nums.push(k);
          else nums.push(a, b);
        } else {
          nums.push(Number(part.trim()));
        }
      }
      continue;
    }
    const lead = /^(\d{1,3})(?=[\s,])/.exec(seg);
    if (lead && /^\d{1,3}\s*,/.test(seg)) {
      nums.push(Number(lead[1]));
      continue;
    }
    return undefined;
  }
  const valid = nums.filter((n) => n > 0);
  return valid.length ? valid : undefined;
}

// ─── Код ───────────────────────────────────────────────────────────────

const MONO_FONT =
  /^(courier new|courier|consolas|lucida console|lucida sans typewriter|menlo|monaco|source code pro|jetbrains mono|fira code|fira mono|liberation mono|dejavu sans mono|cascadia (code|mono)|pt mono|roboto mono|ubuntu mono|noto (sans )?mono|ibm plex mono|sf mono|andale mono)$/i;
const CODE_STYLE = /(code|листинг|код|source|preformatted|программ|script)/i;
const CODE_KEYWORD =
  /^\s*(#include|#define|#!|import\s+[\w.]+|from\s+[\w.]+\s+import|def\s+\w+\s*\(|class\s+\w+|public\s|private\s|protected\s|static\s|return\b|for\s*\(|for\s+\w+\s+in\s|while\s*\(|if\s*\(|else\b|elif\b|switch\s*\(|case\s|int\s+\w|void\s|char\s|float\s|double\s|bool\s|const\s|let\s|var\s|function\s|SELECT\s|INSERT\s|UPDATE\s|DELETE\s|CREATE\s|ALTER\s|DROP\s)/i;

export const isMonospaceFont = (name: string | undefined): boolean => !!name && MONO_FONT.test(name.trim());

/** Насколько строка похожа на исходный код. Русские слова вне комментария снижают оценку. */
function syntaxScore(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  let s = 0;
  if (/[{}]\s*$|^\s*[{}]/.test(t)) s += 2;
  if (/;\s*$/.test(t) && /[=(){}[\]]/.test(t)) s += 2;
  if (CODE_KEYWORD.test(t)) s += 2;
  if (/==|!=|<=|>=|->|=>|\+\+|--|&&|\|\||::/.test(t)) s += 1;
  if (/\w\[\w*\]/.test(t)) s += 1;
  if (/\w\.\w+\(/.test(t)) s += 1;
  if (/[А-Яа-яЁё]{4,}/.test(t) && !/(^|\s)(\/\/|#)/.test(t)) s -= 2;
  return s;
}

/** Абзацы с программным кодом: моноширинный шрифт, стиль «Код», таблица-листинг из одной ячейки. */
function detectCode(model: DocModel): boolean[] {
  const paras = model.paragraphs;
  const code = new Array<boolean>(paras.length).fill(false);
  for (const p of paras) {
    if (p.textBox || p.isEmpty) continue;
    const style = model.styles.get(p.styleId)?.name ?? '';
    if (isMonospaceFont(paraFormat(model, p).dominantFont) || CODE_STYLE.test(style)) code[p.index] = true;
  }
  for (const t of model.tables) {
    if (t.rows.length !== 1 || t.rows[0]!.cells.length !== 1) continue;
    const ps = paras.slice(t.firstParagraph, t.lastParagraph + 1).filter((p) => !p.isEmpty);
    if (!ps.length) continue;
    const mono = ps.filter((p) => code[p.index]).length;
    const syntax = ps.reduce((sum, p) => sum + Math.max(0, syntaxScore(p.text)), 0) / ps.length;
    if (mono / ps.length >= 0.5 || syntax >= 1) for (let i = t.firstParagraph; i <= t.lastParagraph; i++) code[i] = true;
  }
  for (const p of paras) {
    if (!code[p.index] && !p.cell && !p.textBox && syntaxScore(p.text) >= 3) code[p.index] = true;
  }
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < paras.length; i++) {
      const p = paras[i]!;
      if (code[i] || p.isEmpty || p.textBox) continue;
      if ((code[i - 1] || code[i + 1]) && syntaxScore(p.text) >= 1) code[i] = true;
    }
  }
  // Строка между двумя строками кода («'''», «}») или с отступом пробелами рядом с кодом — тоже код.
  const codeNear = (i: number, dir: 1 | -1): boolean => {
    for (let k = i + dir; k >= 0 && k < paras.length; k += dir) if (!paras[k]!.isEmpty) return code[k]!;
    return false;
  };
  for (let i = 0; i < paras.length; i++) {
    const p = paras[i]!;
    if (code[i] || p.isEmpty || p.textBox || p.text.length > 120) continue;
    const before = codeNear(i, -1);
    const after = codeNear(i, 1);
    const indented = /^(?: {2,}|\t)\S/.test(p.text);
    if ((before && after && (indented || !/[А-Яа-яЁё]{4,}/.test(p.text))) || ((before || after) && indented)) code[i] = true;
  }
  for (let i = 1; i + 1 < paras.length; i++) {
    if (!code[i] && paras[i]!.isEmpty && code[i - 1] && code[i + 1]) code[i] = true;
  }
  return code;
}

// ─── Тип работы ────────────────────────────────────────────────────────

function detectKind(model: DocModel, frontEnd: number): DocKind {
  const limit = Math.max(frontEnd, Math.min(model.paragraphs.length, 30));
  const text = model.paragraphs
    .slice(0, limit)
    .map((p) => p.text)
    .join(' ')
    .toLowerCase()
    .replace(/ё/g, 'е');
  if (/выпускн\S*\s+квалификационн|дипломн\S*\s+(работ|проект)/.test(text)) return 'thesis';
  if (/курсов\S*\s+(работ|проект)/.test(text)) return 'coursework';
  if (/лабораторн\S*\s+(работ|практикум)/.test(text)) return 'lab';
  if (/практическ\S*\s+(работ|задани|заняти)/.test(text)) return 'practical';
  if (/контрольн\S*\s+работ/.test(text)) return 'control';
  if (/отчет\S*\s+(о|об|по)\s/.test(text)) return 'report';
  if (frontEnd > 0 && /(^|\s)реферат(\s|$)/.test(text)) return 'essay';
  return 'unknown';
}

export function analyzeStructure(model: DocModel): Structure {
  const paras = model.paragraphs;
  const n = paras.length;
  const roles: Role[] = new Array(n).fill('body');
  const regions: Region[] = new Array(n).fill('main');
  const headings: Heading[] = [];
  const headingByPara = new Map<number, Heading>();
  const captions: Caption[] = [];
  const captionByPara = new Map<number, Caption>();
  const codeAt = detectCode(model);

  const isFlow = (p: Paragraph): boolean => !p.cell && !p.textBox;

  // 1. Кандидаты в заголовки структурных элементов.
  const structuralAt = new Map<number, { kind: StructuralKind; letter?: string; numbered: boolean }>();
  for (const p of paras) {
    if (!isFlow(p) || p.isEmpty || codeAt[p.index]) continue;
    const text = cleanText(p);
    if (p.inToc) {
      // Word кладёт заголовок «Содержание» внутрь блока автособираемого оглавления.
      if (!TOC_LINE_RE.test(p.text) && matchStructural(text) === 'contents') structuralAt.set(p.index, { kind: 'contents', numbered: false });
      continue;
    }
    if (text.length > 160 || TOC_LINE_RE.test(p.text)) continue;
    const firstLine = text.split('\n')[0]!;
    let body = firstLine;
    let numbered = !!autoNumber(p);
    const numMatch = NUMBER_PREFIX_RE.exec(body);
    if (numMatch) {
      body = body.slice(numMatch[0].length);
      numbered = true;
    }
    const appendix = appendixHeading(firstLine);
    if (appendix) {
      structuralAt.set(p.index, { kind: 'appendix', letter: appendix.letter, numbered: false });
      continue;
    }
    if (/^приложение(?![\p{L}])/iu.test(firstLine)) continue;
    const kind = matchStructural(body);
    if (kind) structuralAt.set(p.index, { kind, numbered });
  }

  // 2. Содержание: поле TOC / стили «Оглавление N» или ручные строки с номерами страниц после заголовка.
  const tocEntries: number[] = [];
  let tocAuto = false;
  for (const p of paras) {
    if (p.inToc && !p.isEmpty && !structuralAt.has(p.index)) {
      tocEntries.push(p.index);
      if (p.fields.some((f) => f.type === 'TOC') || model.styles.isTocStyle(p.styleId)) tocAuto = true;
    }
  }
  if (tocEntries.length) tocAuto ||= paras.some((p) => p.inToc && p.fields.some((f) => f.type === 'TOC'));
  for (const [idx, s] of structuralAt) {
    if (s.kind !== 'contents') continue;
    for (let i = idx + 1; i < n; i++) {
      const p = paras[i]!;
      if (p.isEmpty) continue;
      if (p.inToc) {
        tocAuto = true;
        continue;
      }
      if (structuralAt.has(i) && !TOC_LINE_RE.test(p.text)) break;
      if (TOC_LINE_RE.test(p.text)) {
        if (!tocEntries.includes(i)) tocEntries.push(i);
      } else break;
    }
  }
  // Содержание без номеров страниц: строки сразу после «Содержания», которые дальше повторяются заголовками.
  for (const [idx, s] of structuralAt) {
    if (s.kind !== 'contents' || tocEntries.some((i) => i > idx && i < idx + 4)) continue;
    const key = (t: string): string =>
      normalizeTitle(t.replace(/^\s*(?:(?:глава|раздел)\s+\d+\.?|\d{1,2}(?:\.\d{1,2})*\.?)\s*/iu, '').replace(/[\t .…_·]*\d{1,3}\s*$/, ''));
    const block: number[] = [];
    for (let i = idx + 1; i < n && block.length < 80; i++) {
      const p = paras[i]!;
      if (p.isEmpty) continue;
      if (!isFlow(p) || cleanText(p).length > 150 || p.pageBreakBefore) break;
      block.push(i);
    }
    const later = new Set<string>();
    const last = block[block.length - 1] ?? idx;
    for (let i = last + 1; i < n; i++) if (!paras[i]!.isEmpty && isFlow(paras[i]!)) later.add(key(cleanText(paras[i]!)));
    const entries: number[] = [];
    for (const i of block) {
      const k = key(cleanText(paras[i]!));
      // «Приложение А. Техническое задание» в содержании — это «ПРИЛОЖЕНИЕ А» и наименование отдельной строкой.
      const appendixPrefix = /^ПРИЛОЖЕНИЕ\s+[А-ЯA-Z\d]{1,2}(?=[\s.:—–-]|$)/.exec(k)?.[0];
      if (!k || !(later.has(k) || (appendixPrefix && later.has(appendixPrefix)))) break;
      entries.push(i);
    }
    if (entries.length >= 2) for (const i of entries) if (!tocEntries.includes(i)) tocEntries.push(i);
  }
  tocEntries.sort((a, b) => a - b);
  const tocSet = new Set(tocEntries);
  for (const idx of tocSet) structuralAt.delete(idx);

  // 3. Титульная часть — всё до первого структурного заголовка или содержания.
  const firstStructural = [...structuralAt.keys()].sort((a, b) => a - b)[0];
  const firstToc = tocEntries[0];
  let frontEnd = Math.min(firstStructural ?? n, firstToc ?? n);
  if (frontEnd === n) {
    // Структурных заголовков нет: титульным считаем текст до первого разрыва страницы, если он похож на титул.
    const br = paras.findIndex((p, i) => i > 0 && p.pageBreakBefore);
    frontEnd = br > 0 && looksLikeTitle(paras.slice(0, br)) ? br : 0;
  }
  const hasTitlePage = frontEnd > 0 && looksLikeTitle(paras.slice(0, frontEnd));
  if (frontEnd > 0 && !hasTitlePage && frontEnd > 60) frontEnd = 0;

  // 4. Заголовки разделов.
  const levelOf = (p: Paragraph): number | undefined =>
    model.styles.headingLevel(p.styleId) ?? (p.props.outlineLvl !== undefined && p.props.outlineLvl < 9 ? p.props.outlineLvl + 1 : undefined);
  const namedHeading = (p: Paragraph): boolean => !!p.styleId && model.styles.chain(p.styleId).some((s) => /^heading [1-9]$/i.test(s.name));
  // Уровень структуры у множества обычных абзацев (текст из Google Docs, уровень в стиле «Без интервала»)
  // ничего не говорит о заголовках — тогда верим только стилям «Заголовок N».
  const flowParas = paras.filter((p) => isFlow(p) && !p.isEmpty);
  const outlineOnly = flowParas.filter((p) => levelOf(p) !== undefined && !namedHeading(p)).length;
  const outlineNoise = outlineOnly > Math.max(8, flowParas.length * 0.25);
  for (const p of paras) {
    if (!isFlow(p) || p.isEmpty || p.index < frontEnd || tocSet.has(p.index) || codeAt[p.index]) continue;
    const text = cleanText(p);
    const structural = structuralAt.get(p.index);
    const named = namedHeading(p);
    const styleLevel = outlineNoise && !named ? undefined : levelOf(p);
    const auto = autoNumber(p);
    const textNum = NUMBER_PREFIX_RE.exec(text);

    if (structural) {
      headings.push({
        paragraph: p.index,
        level: 1,
        kind: structural.kind === 'appendix' ? 'appendix' : 'structural',
        structural: structural.kind,
        numberDot: false,
        title: text,
        byStyle: styleLevel !== undefined,
        appendixLetter: structural.letter,
        number: structural.numbered ? (auto?.number ?? textNum?.[1]) : undefined,
        numberSource: structural.numbered ? (auto ? 'auto' : 'text') : undefined,
      });
      continue;
    }

    const number = auto?.number ?? textNum?.[1];
    const title = textNum && !auto ? text.slice(textNum[0].length) : text;
    if (styleLevel === undefined && !number) continue;
    if (text.length > 220 || title.length === 0) continue;
    const fmt = paraFormat(model, p);
    const lastChar = title.trimEnd().slice(-1);
    let isHeading = false;
    if (styleLevel !== undefined) isHeading = looksLikeStyledHeading(model, p, title, named);
    else if (number) {
      const boldish = fmt.boldShare > 0.6;
      const shortLine = title.length <= 150 && !/[;,:]$/.test(lastChar);
      const upperStart = /^\p{Lu}/u.test(title);
      isHeading = boldish && shortLine && upperStart && !/^\d/.test(title);
    }
    if (!isHeading) continue;
    const level = number ? number.split('.').length : (styleLevel ?? 1);
    headings.push({
      paragraph: p.index,
      level,
      kind: 'section',
      number,
      numberSource: auto ? 'auto' : number ? 'text' : undefined,
      numberDot: auto ? auto.dot : textNum?.[2] === '.',
      title: title.trim(),
      byStyle: styleLevel !== undefined,
    });
  }

  // 4б. Заголовки без номера и стиля: короткая полностью полужирная строка («Цель работы», «Вывод:»).
  const nearAppendix = new Set<number>();
  for (const [idx, sc] of structuralAt) {
    if (sc.kind !== 'appendix') continue;
    let seen = 0;
    for (let k = idx + 1; k < n && seen < 2; k++) {
      if (paras[k]!.isEmpty) continue;
      nearAppendix.add(k);
      seen++;
    }
  }
  const known = new Set(headings.map((h) => h.paragraph));
  for (const p of paras) {
    const i = p.index;
    if (!isFlow(p) || p.isEmpty || i < frontEnd || tocSet.has(i) || known.has(i) || nearAppendix.has(i) || codeAt[i] || p.list) continue;
    const text = cleanText(p);
    if (text.length > 100 || /[.,;!?]$/.test(text) || /^(Примечани[ея]|где\b)/.test(text)) continue;
    if (FIG_CAPTION_RE.test(text) || TABLE_CAPTION_RE.test(text) || TABLE_CONT_RE.test(text)) continue;
    const fmt = paraFormat(model, p);
    if (fmt.letters < 3 || fmt.boldShare < 0.9) continue;
    headings.push({ paragraph: i, level: 2, kind: 'section', numberDot: false, title: text, byStyle: false, inferred: true });
  }
  headings.sort((a, b) => a.paragraph - b.paragraph);

  // 5. Регионы: содержание, основная часть, список источников, приложения.
  let region: Region = 'main';
  let bibHeading: number | undefined;
  const headingSet = new Map(headings.map((h) => [h.paragraph, h]));
  for (let i = 0; i < n; i++) {
    const h = headingSet.get(i);
    if (h && h.kind !== 'section') {
      if (h.structural === 'contents') region = 'toc';
      else if (h.structural === 'references') {
        region = 'bibliography';
        bibHeading ??= i;
      } else if (h.kind === 'appendix') region = 'appendix';
      else region = 'main';
    } else if (h && !h.inferred && region === 'toc') {
      region = 'main';
    }
    regions[i] = i < frontEnd ? 'front' : tocSet.has(i) ? 'toc' : region;
  }

  // Заголовки-«разделы» внутри списка источников — на самом деле его записи или подгруппы.
  const finalHeadings = headings.filter((h) => !(h.kind === 'section' && regions[h.paragraph] === 'bibliography'));
  for (const h of finalHeadings) headingByPara.set(h.paragraph, h);

  // 6. Роли абзацев.
  for (const p of paras) {
    const i = p.index;
    if (p.textBox) continue;
    if (regions[i] === 'front') {
      roles[i] = p.isEmpty ? 'empty' : 'front';
      continue;
    }
    if (tocSet.has(i)) {
      roles[i] = 'toc';
      continue;
    }
    if (headingByPara.has(i)) {
      roles[i] = 'heading';
      continue;
    }
    if (codeAt[i]) {
      roles[i] = 'code';
      continue;
    }
    if (p.isEmpty) {
      roles[i] = 'empty';
      continue;
    }
    const text = cleanText(p).replace(/^[\ufffc\s]+/, '');
    const fig = FIG_CAPTION_RE.exec(text);
    const tab = !fig ? TABLE_CAPTION_RE.exec(text) : null;
    const cont = !fig && !tab ? TABLE_CONT_RE.exec(text) : null;
    if ((fig || tab || cont) && text.length < 300 && isCaptionContext(model, p, fig ? 'figure' : 'table', !!cont)) {
      const m = (fig ?? tab)!;
      const caption: Caption = cont
        ? {
            paragraph: i,
            kind: 'table',
            continuation: true,
            word: cont[1]!,
            number: cont[3]!.replace(/\s/g, ''),
            sep: '',
            title: '',
            misplaced: false,
          }
        : {
            paragraph: i,
            kind: fig ? 'figure' : 'table',
            continuation: false,
            word: m[1]!,
            number: m[2]!.replace(/\s/g, ''),
            sep: m[4] ?? (m[3] || ''),
            title: (m[5] ?? '').trim(),
            misplaced: false,
          };
      captions.push(caption);
      captionByPara.set(i, caption);
      roles[i] = 'caption';
      continue;
    }
    if (p.cell) {
      roles[i] = 'table-cell';
      continue;
    }
    if (isFigureParagraph(p) && text.replace(/\ufffc/g, '').trim().length < 5) {
      roles[i] = 'figure';
      continue;
    }
    if (p.math?.display) {
      roles[i] = 'formula';
      continue;
    }
    if (/^где(\s|:|$)/.test(text) && i > 0 && isAfterFormula(paras, roles, i)) {
      roles[i] = 'formula-where';
      continue;
    }
    if (regions[i] === 'bibliography') {
      roles[i] = 'bib';
      continue;
    }
    if (/^Примечани[ея]/.test(text)) {
      roles[i] = 'note';
      continue;
    }
    if ((p.list && !headingByPara.has(i)) || /^([-‐-―−•·▪●○]|[а-я]\)|\d{1,2}\))\s/.test(text)) {
      roles[i] = 'list';
      continue;
    }
    roles[i] = 'body';
  }
  // Абзацы, следующие за «ПРИЛОЖЕНИЕ А» (статус и наименование), — часть заголовка приложения.
  for (const h of finalHeadings) {
    if (h.kind !== 'appendix') continue;
    const inline = cleanText(paras[h.paragraph]!).split('\n').length > 1;
    let taken = 0;
    for (let i = h.paragraph + 1; i < n && taken < (inline ? 0 : 2); i++) {
      const p = paras[i]!;
      if (p.isEmpty) continue;
      const t = cleanText(p);
      if (t.length > 200 || headingByPara.has(i) || p.cell) break;
      // Рисунок, формула или код сразу за «ПРИЛОЖЕНИЕ А» — не наименование: его просто нет.
      if (!/\p{L}/u.test(t) || ['figure', 'caption', 'formula', 'code'].includes(roles[i]!)) break;
      if (/^\((обязательное|справочное|рекомендуемое|информационное)\)$/i.test(t) || taken === 0 || roles[i] === 'body') {
        roles[i] = 'appendix-title';
        taken++;
        if (!/^\(/.test(t)) break;
      }
    }
  }

  // 7. Привязка подписей к рисункам и таблицам.
  const figures = paras.filter((p) => !p.textBox && regions[p.index] !== 'front' && isFigureParagraph(p)).map((p) => p.index);
  for (const c of captions) {
    if (c.continuation) continue;
    if (c.kind === 'figure') {
      const before = findNear(paras, c.paragraph, -1, (p) => isFigureParagraph(p), 4);
      const after = findNear(paras, c.paragraph, 1, (p) => isFigureParagraph(p), 1);
      if (before !== undefined) c.target = before;
      else if (after !== undefined) {
        c.target = after;
        c.misplaced = true;
      }
    } else {
      const next = nextNonEmpty(paras, c.paragraph);
      const prev = prevNonEmpty(paras, c.paragraph);
      const cellOf = (idx: number | undefined): Paragraph['cell'] => (idx !== undefined ? paras[idx]!.cell : undefined);
      const own = paras[c.paragraph]!.cell;
      const nextCell = cellOf(next);
      const prevCell = cellOf(prev);
      if (nextCell && (!own || nextCell.table !== own.table)) c.target = nextCell.table;
      else if (prevCell && (!own || prevCell.table !== own.table)) {
        c.target = prevCell.table;
        c.misplaced = true;
      }
    }
  }

  // 8. Таблицы с данными: с границами, хотя бы две строки, не в титуле, не разметка формулы и не листинг кода.
  const dataTables = model.tables
    .filter(
      (t) =>
        !t.nested &&
        t.rows.length >= 2 &&
        t.bordered &&
        regions[t.firstParagraph] !== 'front' &&
        !isLayoutTable(model, t) &&
        !isCodeTable(t, codeAt),
    )
    .map((t) => t.index);

  // 9. Формулы и их номера.
  const formulas: Formula[] = [];
  for (const p of paras) {
    if (p.textBox || regions[p.index] === 'front' || !p.math || codeAt[p.index]) continue;
    const displayLike = p.math.display || (p.cell && cleanText(p).replace(/\ufffc/g, '').trim().length < 3);
    if (!displayLike) continue;
    let number = /\(((?:[А-ЯA-Z]\.)?\d{1,3}(?:[.,]\d{1,3})*)\)\s*$/.exec(p.text)?.[1];
    if (!number && p.cell) {
      const row = model.tables[p.cell.table]?.rows[p.cell.row];
      for (const cell of row?.cells ?? []) {
        for (const b of cell.blocks) {
          if (b.kind !== 'paragraph') continue;
          const m = /^\s*\(((?:[А-ЯA-Z]\.)?\d{1,3}(?:[.,]\d{1,3})*)\)\s*$/.exec(b.text);
          if (m) number = m[1];
        }
      }
    }
    formulas.push({ paragraph: p.index, number });
    if (p.cell) roles[p.index] = 'formula';
  }

  // 10. Список источников.
  let bibliography: Structure['bibliography'];
  if (bibHeading !== undefined) {
    const entries: BibEntry[] = [];
    for (let i = bibHeading + 1; i < n && regions[i] === 'bibliography'; i++) {
      const p = paras[i]!;
      if (p.isEmpty || headingByPara.has(i) || p.textBox || codeAt[i]) continue;
      const text = cleanText(p);
      const auto = p.list && !p.list.bullet ? p.list : undefined;
      const manual = /^\s*\[?(\d{1,3})(?:[.)\]]\s*|\s+)(?=\S)/.exec(text);
      entries.push({
        paragraph: i,
        number: auto ? auto.value : manual ? Number(manual[1]) : undefined,
        label: auto ? auto.label : manual ? manual[0].trim() : undefined,
        auto: !!auto,
        text: auto || !manual ? text : text.slice(manual[0].length),
      });
    }
    bibliography = { heading: bibHeading, entries };
  }

  // 11. Ссылки на источники в тексте (в коде квадратные скобки — это индексы массивов).
  const citations: Citation[] = [];
  for (const p of paras) {
    const r = regions[p.index];
    if (r === 'front' || r === 'toc' || r === 'bibliography' || p.textBox || roles[p.index] === 'code') continue;
    CITATION_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    let prev: Citation | undefined;
    while ((m = CITATION_RE.exec(p.text))) {
      const numbers = parseCitation(m[1]!);
      if (!numbers) continue;
      const cit: Citation = { paragraph: p.index, start: m.index, end: m.index + m[0].length, numbers, raw: m[0] };
      // «[1]–[4]» — диапазон, записанный двумя ссылками.
      if (prev && /^\s*[-–—]\s*$/.test(p.text.slice(prev.end, cit.start)) && prev.numbers.length === 1 && numbers.length === 1) {
        const a = prev.numbers[0]!;
        const b = numbers[0]!;
        if (b > a + 1 && b - a <= 50) for (let k = a + 1; k < b; k++) prev.numbers.push(k);
      }
      citations.push(cit);
      prev = cit;
    }
  }

  return {
    kind: detectKind(model, frontEnd),
    roles,
    regions,
    headings: finalHeadings,
    headingByPara,
    captions,
    captionByPara,
    figures,
    dataTables,
    formulas,
    bibliography,
    citations,
    tocEntries,
    tocAuto,
    frontEnd,
    hasTitlePage,
  };
}

const TITLE_WORDS =
  /(МИНИСТЕРСТВО|МИНОБРНАУКИ|УНИВЕРСИТЕТ|ИНСТИТУТ|АКАДЕМИЯ|КОЛЛЕДЖ|КАФЕДРА|ФАКУЛЬТЕТ|КУРСОВАЯ|ВЫПУСКНАЯ|ДИПЛОМН|КВАЛИФИКАЦИОННАЯ|ОТЧ[ЕЁ]Т|РЕФЕРАТ|ЛАБОРАТОРН|Выполнил|Проверил|Руководитель|Студент|студентка|Научный руководитель|Направление подготовки)/i;

function looksLikeTitle(paras: Paragraph[]): boolean {
  let hits = 0;
  for (const p of paras) if (TITLE_WORDS.test(p.text)) hits++;
  return hits >= 2 || (hits >= 1 && paras.some((p) => /\b(19|20)\d{2}\b/.test(p.text)));
}

function nextNonEmpty(paras: Paragraph[], i: number): number | undefined {
  for (let k = i + 1; k < paras.length; k++) if (!paras[k]!.isEmpty) return k;
  return undefined;
}

function prevNonEmpty(paras: Paragraph[], i: number): number | undefined {
  for (let k = i - 1; k >= 0; k--) if (!paras[k]!.isEmpty) return k;
  return undefined;
}

function findNear(paras: Paragraph[], from: number, dir: 1 | -1, pred: (p: Paragraph) => boolean, maxSteps: number): number | undefined {
  let steps = 0;
  for (let k = from + dir; k >= 0 && k < paras.length; k += dir) {
    const p = paras[k]!;
    if (p.isEmpty) continue;
    if (pred(p)) return k;
    if (++steps >= maxSteps) break;
  }
  return undefined;
}

/** Абзац сразу после формулы — отдельной или в таблице-разметке «формула | (1)». */
function isAfterFormula(paras: Paragraph[], roles: Role[], i: number): boolean {
  const prev = prevNonEmpty(paras, i);
  if (prev === undefined) return false;
  if (roles[prev] === 'formula' || paras[prev]!.math) return true;
  const table = paras[prev]!.cell?.table;
  if (table === undefined) return false;
  for (let k = prev; k >= 0 && paras[k]!.cell?.table === table; k--) if (paras[k]!.math) return true;
  return false;
}

/** Подпись ли это, а не фраза в тексте вида «Рисунок 3 показывает…». */
function isCaptionContext(model: DocModel, p: Paragraph, kind: 'figure' | 'table', continuation: boolean): boolean {
  if (continuation) return true;
  if (model.styles.isCaptionStyle(p.styleId)) return true;
  if (p.fields.some((f) => f.type === 'SEQ')) return true;
  const paras = model.paragraphs;
  const text = cleanText(p);
  const prev = prevNonEmpty(paras, p.index);
  const next = nextNonEmpty(paras, p.index);
  if (kind === 'figure') {
    if ((prev !== undefined && isFigureParagraph(paras[prev]!)) || (next !== undefined && isFigureParagraph(paras[next]!))) return true;
  } else if (next !== undefined && paras[next]!.cell && (!p.cell || paras[next]!.cell!.table !== p.cell.table)) {
    return true;
  }
  const dashSep = /^\S+\s*№?\s*\S+\s*[-‐-―−]\s*\S/.test(text);
  return (p.props.jc === 'center' || dashSep) && !/[.!?]\s*$/.test(text.slice(-1)) && text.length < 200;
}

/** Таблица-разметка: без данных, используется для выравнивания формулы и её номера. */
function isLayoutTable(model: DocModel, t: Table): boolean {
  const paras = model.paragraphs.slice(t.firstParagraph, t.lastParagraph + 1);
  return paras.some((p) => p.math) && t.rows.length <= 3;
}

const LIST_START_RE = /^[•·▪●○■□◦➢✓❖–—-]\s/;

/**
 * Абзац со стилем заголовка или уровнем структуры, который на заголовок не похож:
 * строка с одним рисунком, подпись, элемент списка. Уровень структуры без стиля
 * «Заголовок» часто остаётся случайно (копирование из интернета, Google Docs),
 * поэтому от такого абзаца требуется ещё и форма заголовка: короткая строка без точки в конце.
 */
function looksLikeStyledHeading(model: DocModel, p: Paragraph, title: string, named: boolean): boolean {
  const plain = title.split(OBJ).join('').trim();
  if (letterCount(plain) < 2 || LIST_START_RE.test(plain) || FIG_CAPTION_RE.test(plain) || TABLE_CAPTION_RE.test(plain)) return false;
  if (named) return true;
  if (plain.length > 120 || /[.!?;:,]$/.test(plain) || letterCount(plain) < 3) return false;
  // Без выделения на заголовок похожа только короткая строка без запятых: «Вариант 4», а не фраза из текста.
  return paraFormat(model, p).boldShare > 0.6 || (plain.split(/\s+/).length <= 8 && !plain.includes(','));
}

/** Таблица, в которой лежит только код (листинг). */
function isCodeTable(t: Table, codeAt: boolean[]): boolean {
  for (let i = t.firstParagraph; i <= t.lastParagraph; i++) if (!codeAt[i]) return false;
  return t.lastParagraph >= t.firstParagraph;
}

/** Текст с объектами без символов-заместителей — для сообщений. */
export function displayText(p: Paragraph): string {
  return collapseSpaces(p.text.split(OBJ).join(' '));
}
